/* lf-recipe-grid — in-place tag filtering and recipe search.

   Two filters share one grid and they combine rather than reset each other.

   Tags are server truth. Every pill is a real link to /blogs/{blog}/tagged/{tag}
   that works with JavaScript off; this file upgrades it to a Section Rendering
   API swap — fetch the target URL with ?section_id=, replace
   [data-lf-recipe-swap] with the fetched one, push the URL — so a pill click
   never reloads the page. Same mechanism as lf-collection.

   Search has to be client side. Liquid cannot read a query string (request
   exposes design_mode / host / locale / origin / page_type / path /
   visual_preview_mode and nothing else, and the `search` object only exists on
   the search template), so `?q=` is invisible to the server on a blog page.
   The section publishes every article it can reach as an inert <template>
   index and the matching happens here. Without JS the form is still a real GET
   to the grid's anchor, so the visitor lands on the full list rather than a
   broken one.

   The two compose because the index is whatever the active tag left behind: a
   tagged URL renders a tag-scoped blog.articles, so searching inside it
   searches inside the tag. The URL carries both
   (/blogs/recipes/tagged/beef?q=slow) and both stay visible in the UI — the
   pill keeps its active state and the result bar names the term.

   The hero owns the input and this section owns the behaviour, so the two are
   wired by attribute contract: [data-lf-recipe-search-form] and
   [data-lf-recipe-search-input] live in lf-recipe-search-hero.liquid. */
(function () {
  var ROOT = '[data-lf-recipe-grid]';
  var SWAP = '[data-lf-recipe-swap]';

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function queryOf(search) {
    try {
      var value = new URLSearchParams(search).get('q');
      return value ? value.trim() : '';
    } catch (error) {
      return '';
    }
  }

  function init(root) {
    if (root.dataset.lfReady) return;
    root.dataset.lfReady = 'true';

    var sectionId = root.getAttribute('data-section-id');
    var blogPath = root.getAttribute('data-blog-url') || window.location.pathname;
    var currentPath = window.location.pathname;
    var term = queryOf(window.location.search);
    var request = null;

    function sectionUrl(url) {
      var next = new URL(url, window.location.href);
      /* The server has no use for the search term and caches better without
         it, so only the path and its own params are fetched. */
      next.searchParams.delete('q');
      next.searchParams.set('section_id', sectionId);
      return next.toString();
    }

    /* The address bar carries both filters: the path is the tag, ?q= is the
       search. Anything else the target URL already held (page, mainly) rides
       along untouched. */
    function stateUrl(url) {
      var next = new URL(url, window.location.href);
      next.searchParams.delete('q');
      next.searchParams.delete('section_id');
      if (term) next.searchParams.set('q', term);
      return next.pathname + next.search;
    }

    function writeUrl(url, replace) {
      if (!window.history || !window.history.pushState) return;
      var value = stateUrl(url);
      var state = { lfRecipes: true };
      if (replace) window.history.replaceState(state, '', value);
      else window.history.pushState(state, '', value);
    }

    /* Every recipe search field on the page shows the term the grid is actually
       filtering by, so a reload or a shared link arrives with the box filled. */
    function syncFields() {
      var fields = document.querySelectorAll('[data-lf-recipe-search-input]');
      for (var i = 0; i < fields.length; i++) fields[i].value = term;
    }

    /* Re-run after every swap: the result bar, the results list and the index
       all live inside the swapped region, so they are new elements each time. */
    function render() {
      var results = root.querySelector('[data-lf-recipe-results]');
      var none = root.querySelector('[data-lf-recipe-noresults]');
      var bar = root.querySelector('[data-lf-recipe-resultbar]');
      var label = root.querySelector('[data-lf-recipe-term]');
      var index = root.querySelector('[data-lf-recipe-index]');

      if (results) {
        results.innerHTML = '';
        results.hidden = true;
      }
      if (none) none.hidden = true;
      if (bar) bar.hidden = true;

      if (!term) {
        root.removeAttribute('data-lf-searching');
        return;
      }

      root.setAttribute('data-lf-searching', '');
      if (label) label.textContent = term;
      if (bar) bar.hidden = false;

      if (!results || !none || !index) return;

      /* Every word has to appear somewhere in the card's terms, so a two-word
         query narrows instead of widening. */
      var words = term.toLowerCase().split(/\s+/);
      var items = index.content.querySelectorAll('[data-lf-recipe-terms]');
      var found = 0;

      for (var i = 0; i < items.length; i++) {
        var haystack = items[i].getAttribute('data-lf-recipe-terms') || '';
        var match = true;

        for (var w = 0; w < words.length; w++) {
          if (words[w] && haystack.indexOf(words[w]) === -1) {
            match = false;
            break;
          }
        }

        if (match) {
          results.appendChild(items[i].cloneNode(true));
          found++;
        }
      }

      if (found) results.hidden = false;
      else none.hidden = false;
    }

    function scrollToGrid() {
      /* scrollIntoView walks to whichever ancestor actually scrolls, so this is
         correct on both sides of the 990px axis where Horizon hands the
         scrollport from document.scrollingElement to .page-wrapper. */
      root.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    }

    function navigate(url, options) {
      var opts = options || {};
      var swap = root.querySelector(SWAP);
      if (!swap) return;

      if (request) request.abort();
      request = new AbortController();
      root.setAttribute('data-lf-loading', '');

      fetch(sectionUrl(url), {
        signal: request.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.querySelector(SWAP);
          if (!next) throw new Error('no swap region');

          swap.innerHTML = next.innerHTML;
          currentPath = new URL(url, window.location.href).pathname;

          render();
          if (opts.write !== false) writeUrl(url, opts.replace);
          if (opts.scroll) scrollToGrid();
        })
        .catch(function (error) {
          /* An aborted request is a newer click winning, not a failure. A real
             failure falls back to a full page load so the click still works. */
          if (error && error.name === 'AbortError') return;
          window.location.assign(url);
        })
        .finally(function () {
          request = null;
          root.removeAttribute('data-lf-loading');
        });
    }

    root.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;

      var clear = event.target.closest('[data-lf-recipe-clear]');
      if (clear && root.contains(clear)) {
        event.preventDefault();
        /* Clears the search only. The tag is a separate filter and the pill
           row is the affordance for dropping that one. */
        term = '';
        syncFields();
        render();
        writeUrl(window.location.href);
        return;
      }

      var pill = event.target.closest('[data-lf-recipe-pill]');
      if (pill && root.contains(pill) && pill.getAttribute('href')) {
        event.preventDefault();
        navigate(pill.getAttribute('href'), {});
        return;
      }

      var page = event.target.closest('.lf-pagination a[href]');
      if (page && root.contains(page)) {
        event.preventDefault();
        navigate(page.getAttribute('href'), { scroll: true });
      }
    });

    /* The form is a real GET; intercepting it keeps the results in place on the
       branded page instead of reloading to an unstyled search template. */
    document.addEventListener('submit', function (event) {
      /* The listener outlives the root when the theme editor re-renders the
         section, so the detached copy has to stand down for the new one. */
      if (!root.isConnected) return;

      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-lf-recipe-search-form]')) return;

      var field = form.querySelector('[data-lf-recipe-search-input]');
      if (!field) return;

      event.preventDefault();
      term = field.value.trim();
      render();
      writeUrl(window.location.href);
      scrollToGrid();
    });

    /* Only entries this section pushed are handled in place; anything else in
       history is a different page and must load normally. A tag change moves
       the path, so the path is measured against the blog rather than pinned. */
    window.addEventListener('popstate', function () {
      if (!root.isConnected) return;

      var path = window.location.pathname;
      if (path !== blogPath && path.indexOf(blogPath + '/tagged/') !== 0) return;

      term = queryOf(window.location.search);
      syncFields();

      /* Same tag, different term: nothing needs fetching. */
      if (path === currentPath) {
        render();
        return;
      }

      navigate(window.location.href, { write: false });
    });

    syncFields();
    render();
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
